import React, { useState } from 'react';
import { toast } from 'sonner';
import { CalendarPlus, Download, ExternalLink, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { errMsg } from '../../lib/aiInterviewApi';
import { candidateScheduleAPI, fmtSlot } from '../../lib/schedulingApi';

const gcalUrl = (ctx) => {
  const z = (iso) => new Date(`${iso}Z`).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const link = ctx.join_path ? `${window.location.origin}${ctx.join_path}` : ctx.meeting_link;
  const q = new URLSearchParams({ action: 'TEMPLATE', text: `${ctx.title}: ${ctx.job_title}`, dates: `${z(ctx.start)}/${z(ctx.end)}`,
    details: link ? `Join: ${link}` : 'Scheduled with CHIRE', location: link || '' });
  return `https://calendar.google.com/calendar/render?${q}`;
};

export default function BookedCard({ ctx, token, tz, onReschedule, onChanged }) {
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const d = new Date(`${ctx.start}Z`);
  const cancel = async () => {
    setBusy(true);
    try {
      await candidateScheduleAPI.cancel(token, reason);
      toast.success('Interview cancelled');
      onChanged();
    } catch (e) {
      toast.error(errMsg(e, 'Could not cancel'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="iv-card" data-testid="booked-card">
      <div className="iv-eyebrow" style={{ color: '#047857' }}>You're booked</div>
      <div className="sc-when" style={{ marginTop: '0.6rem' }}>
        <div className="sc-date"><span>{d.toLocaleDateString(undefined, { timeZone: tz, month: 'short' })}</span><b>{d.toLocaleDateString(undefined, { timeZone: tz, day: 'numeric' })}</b></div>
        <div>
          <div style={{ fontWeight: 800, color: '#002A38', fontSize: '1.05rem' }} data-testid="booked-time">{fmtSlot(ctx.start, tz, { year: 'numeric' })}</div>
          <div className="iv-small">{ctx.duration_min} minutes · {tz}{ctx.interviewers?.length ? ` · with ${ctx.interviewers.join(', ')}` : ''}</div>
        </div>
      </div>
      <div className="iv-row" style={{ marginTop: '1rem' }}>
        {ctx.join_path ? <Link className="iv-btn iv-btn-cta iv-btn-sm" to={ctx.join_path} data-testid="booked-join-room-link"><Video size={14} /> {ctx.room_state === 'open' ? 'Join interview now' : 'Interview room'}</Link>
          : ctx.meeting_link ? <a className="iv-btn iv-btn-primary iv-btn-sm" href={ctx.meeting_link} target="_blank" rel="noreferrer" data-testid="booked-join-link"><ExternalLink size={14} /> Join link</a>
          : <span className="iv-small" data-testid="booked-no-link">The hiring team will send the meeting link.</span>}
        <a className="iv-btn iv-btn-ghost iv-btn-sm" href={candidateScheduleAPI.icsUrl(token)} data-testid="booked-ics-link"><Download size={14} /> Add to calendar (.ics)</a>
        <a className="iv-btn iv-btn-ghost iv-btn-sm" href={gcalUrl(ctx)} target="_blank" rel="noreferrer" data-testid="booked-gcal-link"><CalendarPlus size={14} /> Google Calendar</a>
      </div>
      {ctx.can_reschedule ? (
        <div className="iv-row" style={{ marginTop: '1rem', borderTop: '1px solid #EEF2F5', paddingTop: '0.9rem' }}>
          <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={onReschedule} data-testid="booked-reschedule-btn">Pick another time</button>
          <button type="button" className="iv-btn iv-btn-danger iv-btn-sm" onClick={() => setCancelling((c) => !c)} data-testid="booked-cancel-btn">Cancel interview</button>
        </div>
      ) : <p className="iv-small" style={{ marginTop: '0.9rem' }} data-testid="booked-locked-note">Need a change this close to the interview? Reply to your confirmation email and the hiring team will help.</p>}
      {cancelling && (
        <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
          <textarea className="iv-textarea" rows={2} placeholder="Let the team know why (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="booked-cancel-reason" />
          <div className="iv-row-end" style={{ marginTop: 0 }}><button type="button" className="iv-btn iv-btn-danger" disabled={busy} onClick={cancel} data-testid="booked-cancel-confirm-btn">{busy ? 'Cancelling…' : 'Yes, cancel'}</button></div>
        </div>
      )}
    </section>
  );
}
